import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Send, Star, Phone, Globe, Mail, MapPin, Satellite, Map as MapIcon, Plus, Minus, Flame, Check, Clock, Loader2 } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.markercluster';
import 'leaflet.heat';
import api from '../api';
import { cn } from '@/lib/utils';
import './MapScanner.css';

import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// Shared between the filter useEffect (which filter) and the sidebar's
// live per-filter count badges (how many) - one source of truth for which
// gap_labels string each filter id maps to.
const GAP_MAPPING = {
  no_website: 'No Website',
  low_reviews: 'Low Reviews',
  no_phone: 'No Phone',
  no_hours: 'No Hours',
  low_rating: 'Low Rating',
  new_business: 'New Business',
};

// CRA only exposes REACT_APP_-prefixed env vars to the browser bundle - see
// .env's comment next to REACT_APP_GOOGLE_MAPS_API_KEY.
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Esri's free, keyless street map - "Map" mode's tiles until (or unless)
// the real Google roadmap tiles below are ready, so this mode is never
// blank/broken while the Google session request is in flight or if it
// fails (missing key, or the key's Google Cloud project doesn't have the
// Map Tiles API + billing enabled).
const ESRI_STREET_FALLBACK_URL = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}';
const ESRI_STREET_ATTRIBUTION = 'Tiles &copy; Esri &mdash; Esri, HERE, Garmin, USGS, Intermap, INCREMENT P, NRCan, Esri Japan, METI, Esri China (Hong Kong), Esri Korea, Esri (Thailand), NGCC, (c) OpenStreetMap contributors, and the GIS User Community';

// Google Maps Platform's Tiles API needs a short-lived session token before
// any tile URL is valid (unlike Esri's tiles, which are plain stateless
// URLs) - see
// https://developers.google.com/maps/documentation/tile/session_tokens
// Preset quick-pick values for the radius selector - "Custom" (any other
// value, typed into the number input next to them) is what makes the
// radius genuinely arbitrary rather than limited to these four.
const RADIUS_PRESETS_KM = [1, 2, 5, 10];

// localStorage key for "Recently used" business types in the TARGET
// suggestion dropdown - per-browser, not per-account (there's no backend
// endpoint for this and it's not worth one just for a UX nicety).
const RECENT_INDUSTRIES_KEY = 'leadFinder.recentIndustries';
const RECENT_INDUSTRIES_MAX = 5;

// Fallback TARGET suggestion list if GET /scanner/industries fails (offline,
// backend hiccup, etc.) - mirrors scanner_service.INDUSTRY_KEYWORDS' keys on
// the backend so the dropdown still has something reasonable to show, but
// that endpoint (not this list) is the source of truth whenever it's
// reachable, since it's guaranteed to match what /scan actually knows how
// to expand into a Places keyword search.
const FALLBACK_INDUSTRIES = [
  'Restaurants & Cafes', 'Beauty & Salons', 'Gyms & Fitness', 'Medical Clinics',
  'Retail Shops', 'Hotels & Guesthouses', 'Schools & Coaching', 'Real Estate',
  'Automobile Dealers', 'Auto Repair', 'Hardware Stores', 'Pharmacies',
  'Clothing Boutiques', 'Bakeries & Sweet Shops', 'Dental Clinics',
  'Grocery & Kirana', 'Electronics Shops', 'Jewellery Shops', 'Furniture Stores',
  'Travel Agents', 'Photography Studios', 'Plumbing Services', 'Electricians',
  'HVAC & AC Services', 'Pest Control', 'Cleaning Services',
  'Contractors & Builders', 'Painters & Decorators', 'Carpenters & Woodwork',
  'Security Services', 'Courier & Logistics', 'CA & Accounting Firms',
  'Lawyers & Legal Services', 'Digital Marketing Agencies', 'Event Management',
  'Caterers & Banquets', 'Printing & Signage', 'Driving Schools',
  'Tutors & Coaching', 'Veterinary Clinics', 'Opticians & Eye Care',
];

async function createGoogleTileSession(apiKey) {
  const res = await fetch(`https://tile.googleapis.com/v1/createSession?key=${apiKey}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mapType: 'roadmap', language: 'en-US', region: 'IN' }),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`Google Tiles session request failed (${res.status}): ${body}`);
  }
  return res.json(); // { session, expiry, tileWidth, tileHeight, imageFormat }
}

export default function MapScanner() {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [city, setCity] = useState('');
  const [type, setType] = useState('');

  // Radius (km, converted to meters at call sites) - user-adjustable via the
  // preset buttons or the custom number input, see the RADIUS section below.
  const [radiusKm, setRadiusKm] = useState(5);
  // "Full City" - ignores the radius circle and scans the whole city/area
  // instead (backend: deep_scan_places' wide 3x3-grid search, which only
  // takes a city name, not lat/lng - see leads_finder/services/scanner.py).
  // This *is* what the old "Deep Scan Mode" toggle already sent as
  // `deep_scan` - renamed/repositioned next to the radius controls instead
  // of kept as a second, separately-labelled toggle bound to the exact same
  // backend flag (which would've just been two switches for one thing).
  const [fullCity, setFullCity] = useState(false);

  // The actual scan/circle center - set by picking a LOCATION suggestion,
  // clicking the map, or (after a scan) wherever the backend geocoded `city`
  // to. Kept separate from `city` (free text) because a map click has no
  // corresponding place name to put in that field.
  const [selectedLocation, setSelectedLocation] = useState(null); // {lat, lng}

  // LOCATION field autocomplete state.
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const locationDebounceRef = useRef(null);
  // Guards against re-opening the dropdown for the query that a selection
  // itself just set into `city` (setCity(description) below would otherwise
  // immediately re-trigger the autocomplete effect).
  const suppressLocationFetchRef = useRef(false);
  const locationBoxRef = useRef(null);

  // TARGET field suggestion state (recently-used + full industry list).
  const [industries, setIndustries] = useState(FALLBACK_INDUSTRIES);
  const [recentIndustries, setRecentIndustries] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(RECENT_INDUSTRIES_KEY) || '[]');
      return Array.isArray(raw) ? raw : [];
    } catch {
      return [];
    }
  });
  const [showTargetSuggestions, setShowTargetSuggestions] = useState(false);
  const targetBoxRef = useRef(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  // Clustered instead of a plain layerGroup - a dense scan (50-200+ leads)
  // used to render as a wall of overlapping dots; this collapses nearby
  // markers into count bubbles until you zoom in, re-themed via
  // MapScanner.css (.marker-cluster-custom) to match the page instead of
  // the plugin's default yellow/orange.
  const markerGroup = useRef(
    L.markerClusterGroup({
      maxClusterRadius: 60,
      spiderfyOnMaxZoom: true,
      iconCreateFunction: (cluster) => {
        const count = cluster.getChildCount();
        const sizeClass = count >= 50 ? 'cluster-lg' : count >= 15 ? 'cluster-md' : '';
        return L.divIcon({
          html: `<div>${count}</div>`,
          className: `marker-cluster-custom ${sizeClass}`,
          iconSize: L.point(40, 40),
        });
      },
    })
  );
  const [coordinates, setCoordinates] = useState('30.7046, 76.7179');

  // Satellite (default) vs. Map base-layer toggle. Satellite is paired with
  // a transparent place/road-name overlay (raw Esri World_Imagery on its
  // own has zero labels) so names are visible in both modes, not just Map.
  const [mapMode, setMapMode] = useState('satellite'); // 'satellite' | 'street'
  const satelliteLayer = useRef(null);
  const satelliteLabels = useRef(null);
  const streetLayer = useRef(null);

  // Heatmap of scanned-lead density - built from whatever's currently
  // passing the active GAP filters (see the filter useEffect below), so it
  // always matches what the cluster markers are showing.
  const [showHeatmap, setShowHeatmap] = useState(false);
  const heatLayer = useRef(null);

  // Real search-radius preview - replaces the old hardcoded/decorative
  // geofence circle. Drawn at the actual geocoded center the last scan
  // returned, at the actual `radius` (meters) sent to the backend - not a
  // fixed screen position, and it actually reflects the search that ran.
  const radiusCircle = useRef(null);

  const [filters, setFilters] = useState([
    { id: 'no_website', label: 'No Website', color: 'bg-rose-500', active: true },
    { id: 'low_reviews', label: 'Low Reviews', color: '', active: true },
    { id: 'no_phone', label: 'No Phone', color: 'bg-blue-500', active: true },
    { id: 'no_hours', label: 'No Hours', color: 'bg-purple-500', active: true },
    { id: 'low_rating', label: 'Low Rating', color: '', active: true },
    { id: 'new_business', label: 'New Business', color: 'bg-emerald-400', active: false },
  ]);

  useEffect(() => {
    if (map.current || !mapContainer.current) return;

    map.current = L.map(mapContainer.current, {
      center: [30.7046, 76.7179],
      zoom: 13,
      zoomControl: false,
    });

    // Satellite base (aerial imagery, no labels of its own).
    satelliteLayer.current = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
      attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
    });
    // Transparent place/road/city-name overlay - stacks on top of the
    // satellite imagery above so business/street names are actually
    // readable (matches Google Maps' "Hybrid" look), not just in Map mode.
    satelliteLabels.current = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}', {
      attribution: 'Labels &copy; Esri',
      pane: 'shadowPane', // renders above tile/overlay panes, below markers
      className: 'satellite-labels-pane', // legibility drop-shadow, see MapScanner.css
    });
    // Map base ("standard" road map) - starts on Esri's free street tiles
    // (works immediately, no key needed) and gets swapped for the real
    // Google roadmap tiles below as soon as that session is ready, so this
    // mode is never blank while that request is in flight.
    streetLayer.current = L.tileLayer(ESRI_STREET_FALLBACK_URL, {
      attribution: ESRI_STREET_ATTRIBUTION,
      maxZoom: 19,
    });

    satelliteLayer.current.addTo(map.current);
    satelliteLabels.current.addTo(map.current);

    // Swap "Map" mode's tiles over to real Google Maps roadmap tiles (via
    // the Tiles API session above) once that request resolves - works
    // whether the user is already on street mode or switches to it later.
    // Falls back to (and stays on) the Esri layer above if there's no key,
    // or the request fails (most likely cause: the Map Tiles API isn't
    // enabled, or billing isn't set up, on that key's Google Cloud project
    // - see https://console.cloud.google.com/google/maps-apis/api-list).
    if (GOOGLE_MAPS_API_KEY) {
      createGoogleTileSession(GOOGLE_MAPS_API_KEY)
        .then(({ session }) => {
          if (!map.current) return; // unmounted before this resolved
          const googleLayer = L.tileLayer(
            `https://tile.googleapis.com/v1/2dtiles/{z}/{x}/{y}?session=${session}&key=${GOOGLE_MAPS_API_KEY}`,
            { attribution: '&copy; Google', maxZoom: 21 }
          );
          const fallbackWasVisible = map.current.hasLayer(streetLayer.current);
          if (fallbackWasVisible) map.current.removeLayer(streetLayer.current);
          streetLayer.current = googleLayer;
          if (fallbackWasVisible) streetLayer.current.addTo(map.current);
        })
        .catch((err) => {
          console.error('Google Maps tile session failed - "Map" mode is staying on the Esri fallback street tiles:', err);
        });
    } else {
      console.warn('REACT_APP_GOOGLE_MAPS_API_KEY is not set - "Map" mode is using the Esri fallback street tiles.');
    }

    // No L.control.zoom here - replaced by the custom zoom buttons in the
    // top-right toolbar (JSX below) so they can sit alongside the
    // satellite/map toggle instead of floating separately at the bottom.

    markerGroup.current.addTo(map.current);

    map.current.on('move', () => {
      const center = map.current.getCenter();
      setCoordinates(`${center.lat.toFixed(4)}, ${center.lng.toFixed(4)}`);
    });

    // Clicking anywhere on the map re-centers the radius circle there (see
    // the selectedLocation/radiusKm effect below, which actually draws it) -
    // an explicit, precise way to pick a scan center beyond typing a
    // location name.
    map.current.on('click', (e) => {
      setSelectedLocation({ lat: e.latlng.lat, lng: e.latlng.lng });
    });

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  const [status, setStatus] = useState('');

  const toggleMapMode = () => {
    if (!map.current) return;
    const next = mapMode === 'satellite' ? 'street' : 'satellite';
    if (next === 'street') {
      map.current.removeLayer(satelliteLayer.current);
      map.current.removeLayer(satelliteLabels.current);
      streetLayer.current.addTo(map.current);
    } else {
      map.current.removeLayer(streetLayer.current);
      satelliteLayer.current.addTo(map.current);
      satelliteLabels.current.addTo(map.current);
    }
    setMapMode(next);
  };

  // Draws (and redraws, never accumulates) the radius-preview circle at
  // selectedLocation, sized to radiusKm - reacts live to both a new center
  // (search suggestion picked / map clicked / scan completed) and a radius
  // change (preset button or the custom input), so what's on the map always
  // matches what a scan would actually use. Skipped entirely in Full City
  // mode - the circle would be misleading there since deep_scan_places
  // ignores it and searches the whole city instead.
  useEffect(() => {
    if (!map.current) return;
    if (radiusCircle.current) {
      map.current.removeLayer(radiusCircle.current);
      radiusCircle.current = null;
    }
    if (!selectedLocation || fullCity) return;
    radiusCircle.current = L.circle([selectedLocation.lat, selectedLocation.lng], {
      radius: radiusKm * 1000,
      color: '#00c3ff',
      weight: 2,
      dashArray: '6 6',
      fillColor: '#00a2ff',
      fillOpacity: 0.325,
      interactive: false,
    }).addTo(map.current);
  }, [selectedLocation, radiusKm, fullCity]);

  // TARGET suggestions: fetch the real industry list once (auth-gated, same
  // as /scan) - falls back silently to FALLBACK_INDUSTRIES (already the
  // initial state) if this fails.
  useEffect(() => {
    api.get('/scanner/industries')
      .then((res) => {
        if (Array.isArray(res.data?.industries) && res.data.industries.length > 0) {
          setIndustries(res.data.industries);
        }
      })
      .catch((err) => console.warn('Could not load industry list, using fallback:', err));
  }, []);

  // LOCATION suggestions: debounced Places Autocomplete as the user types.
  useEffect(() => {
    if (suppressLocationFetchRef.current) {
      suppressLocationFetchRef.current = false;
      return;
    }
    if (locationDebounceRef.current) clearTimeout(locationDebounceRef.current);
    const query = city.trim();
    if (query.length < 2) {
      setLocationSuggestions([]);
      return;
    }
    locationDebounceRef.current = setTimeout(async () => {
      setLocationLoading(true);
      try {
        const res = await api.get('/scanner/location-suggestions', { params: { query } });
        setLocationSuggestions(res.data?.suggestions || []);
        setShowLocationSuggestions(true);
      } catch (err) {
        console.error('Location autocomplete failed', err);
      } finally {
        setLocationLoading(false);
      }
    }, 350);
    return () => clearTimeout(locationDebounceRef.current);
  }, [city]);

  // Close both suggestion dropdowns on an outside click - typing/focusing
  // opens them, but nothing was closing them again besides picking a value.
  useEffect(() => {
    const onDocClick = (e) => {
      if (locationBoxRef.current && !locationBoxRef.current.contains(e.target)) {
        setShowLocationSuggestions(false);
      }
      if (targetBoxRef.current && !targetBoxRef.current.contains(e.target)) {
        setShowTargetSuggestions(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Resolves a picked LOCATION suggestion to lat/lng (Autocomplete
  // predictions don't carry coordinates themselves - see
  // scanner_service.resolve_place) and flies the map there.
  const selectLocationSuggestion = useCallback(async (suggestion) => {
    suppressLocationFetchRef.current = true;
    setCity(suggestion.description);
    setShowLocationSuggestions(false);
    setLocationSuggestions([]);
    try {
      const res = await api.get('/scanner/location-details', { params: { place_id: suggestion.place_id } });
      const { lat, lng } = res.data || {};
      if (lat && lng) {
        setSelectedLocation({ lat, lng });
        if (map.current) map.current.flyTo([lat, lng], 13);
      }
    } catch (err) {
      console.error('Could not resolve picked location', err);
    }
  }, []);

  const handleLocationKeyDown = (e) => {
    if (e.key === 'Enter' && showLocationSuggestions && locationSuggestions.length > 0) {
      e.preventDefault();
      selectLocationSuggestion(locationSuggestions[0]);
    } else if (e.key === 'Escape') {
      setShowLocationSuggestions(false);
    }
  };

  // Picks a TARGET industry - from either the "Recently used" chips or the
  // full filtered list - and bumps it to the front of the recently-used
  // list (deduped, capped at RECENT_INDUSTRIES_MAX), persisted so it
  // survives a refresh.
  const selectIndustry = (name) => {
    setType(name);
    setShowTargetSuggestions(false);
    setRecentIndustries((prev) => {
      const next = [name, ...prev.filter((n) => n !== name)].slice(0, RECENT_INDUSTRIES_MAX);
      try {
        localStorage.setItem(RECENT_INDUSTRIES_KEY, JSON.stringify(next));
      } catch {
        // localStorage unavailable (private mode, quota, etc.) - the
        // dropdown just won't remember this session's picks, nothing else
        // depends on it.
      }
      return next;
    });
  };

  const filteredIndustries = useMemo(() => {
    const q = type.trim().toLowerCase();
    if (!q) return industries;
    return industries.filter((i) => i.toLowerCase().includes(q));
  }, [industries, type]);

  const handleScan = async () => {
    // Full City needs a city name (deep_scan_places geocodes it server-side
    // and has no lat/lng entry point); the radius-scoped path is happy with
    // either a picked/clicked point or a typed city name.
    if (fullCity ? !city.trim() : !city.trim() && !selectedLocation) return;
    setLoading(true);
    setStatus(fullCity ? 'Scanning the full city...' : 'Scanning selected area...');
    setLeads([]);
    markerGroup.current.clearLayers();

    try {
      setStatus(`Scanning for ${type} ${fullCity ? `across ${city}` : `in ${city || 'selected area'}`}...`);
      const params = {
        business_type: type,
        radius: radiusKm * 1000,
        deep_scan: fullCity,
      };
      if (city.trim()) params.city = city.trim();
      // Prefer the exact picked/clicked point over re-geocoding `city` -
      // matches what the radius circle is actually showing. Full City
      // ignores lat/lng entirely on the backend, so it's left out there.
      if (!fullCity && selectedLocation) {
        params.lat = selectedLocation.lat;
        params.lng = selectedLocation.lng;
      }
      const response = await api.get('/scanner/scan', { params, timeout: 180000 });

      const leadsData = response.data?.leads || [];
      setLeads(leadsData);

      // Re-center the radius preview on wherever this scan actually
      // resolved to (relevant when only `city` text was sent) - the
      // effect above redraws the circle whenever selectedLocation changes.
      const scanLocation = response.data?.location;
      if (scanLocation?.lat && scanLocation?.lng) {
        setSelectedLocation({ lat: scanLocation.lat, lng: scanLocation.lng });
        if (map.current) map.current.flyTo([scanLocation.lat, scanLocation.lng], 13);
      }

      if (leadsData.length > 0) {
        setStatus(`Found ${leadsData.length} leads!`);
      } else {
        setStatus('No leads found in this area. Try a broader search or different niche.');
      }
    } catch (err) {
      console.error('Scan failed', err);
      setStatus(err.code === 'ECONNABORTED' ? 'Scan timed out. Try a smaller radius.' : 'Scan failed. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const updateMarkers = (leadsData, fly = true) => {
    markerGroup.current.clearLayers();

    leadsData.forEach((lead) => {
      const markerColor = lead.digital_health_score > 50 ? "#ef4444" : lead.digital_health_score > 20 ? "#f97316" : "#22c55e";

      const customIcon = L.divIcon({
        className: 'custom-div-icon',
        html: `<div style="background-color: ${markerColor}; width: 24px; height: 24px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.3);"></div>`,
        iconSize: [24, 24],
        iconAnchor: [12, 12],
      });

      const starSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="#fbbf24" stroke="#fbbf24" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>`;
      const mapPinSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 15 4 10a8 8 0 0 1 16 0"/><circle cx="12" cy="10" r="3"/></svg>`;
      const phoneSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>`;
      const globeSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/></svg>`;

      const marker = L.marker([lead.location.lat, lead.location.lng], { icon: customIcon })
        .on('click', (e) => {
          // Quick pulse/bounce on the marker itself (see MapScanner.css) -
          // purely a click-feedback touch, the popup below still opens as
          // normal via Leaflet's own default marker-click behavior.
          const el = e.target.getElement?.();
          if (!el) return;
          el.classList.remove('marker-bounce');
          // Force reflow so re-adding the class restarts the animation even
          // on a second click of the same marker.
          // eslint-disable-next-line no-unused-expressions
          el.offsetWidth;
          el.classList.add('marker-bounce');
          setTimeout(() => el.classList.remove('marker-bounce'), 700);
        })
        .bindPopup(`
          <div style="color: #1e293b; padding: 4px; font-family: 'Inter', sans-serif; min-width: 240px;">
            <h3 style="margin: 0 0 10px 0; font-weight: 800; font-size: 16px; line-height: 1.2;">${lead.name}</h3>
            ${lead.rating ? `<div style="display: flex; align-items: center; gap: 6px; margin-bottom: 12px; font-size: 14px; font-weight: 700; color: #334155;">
              ${starSvg} <span>${lead.rating}</span> <span style="color: #94a3b8; font-weight: 500;">(${lead.reviews_count} reviews)</span>
            </div>` : ''}
            <div style="display: flex; flex-direction: column; gap: 10px; font-size: 13px; color: #475569;">
              <div style="display: flex; align-items: flex-start; gap: 8px;">
                <div style="margin-top: 2px; color: #94a3b8; flex-shrink: 0;">${mapPinSvg}</div>
                <span style="line-height: 1.4;">${lead.address || 'Address not available'}</span>
              </div>
              ${lead.phone ? `
              <div style="display: flex; align-items: center; gap: 8px;">
                <div style="color: #94a3b8; flex-shrink: 0;">${phoneSvg}</div>
                <span style="font-weight: 600;">${lead.phone}</span>
              </div>` : ''}
              ${lead.website ? `
              <div style="display: flex; align-items: center; gap: 8px;">
                <div style="color: #94a3b8; flex-shrink: 0;">${globeSvg}</div>
                <a href="${lead.website.startsWith('http') ? lead.website : 'https://' + lead.website}" target="_blank" style="color: #2563eb; text-decoration: none; text-overflow: ellipsis; overflow: hidden; white-space: nowrap; max-width: 190px;">${lead.website.replace(/^https?:\/\//, '')}</a>
              </div>` : ''}
            </div>
            <div style="margin-top: 16px; padding-top: 14px; border-top: 1px solid #f1f5f9; display: flex; justify-content: space-between; align-items: center;">
              <span style="font-size: 11px; font-weight: 800; color: #94a3b8; letter-spacing: 0.05em;">OPPORTUNITY SCORE</span>
              <span style="font-weight: 900; font-size: 18px; color: ${markerColor};">${lead.digital_health_score}</span>
            </div>
          </div>
        `, { maxWidth: 320, className: 'custom-popup' });

      markerGroup.current.addLayer(marker);
    });

    if (fly && leadsData.length > 0 && map.current) {
      map.current.flyTo([leadsData[0].location.lat, leadsData[0].location.lng], 14);
    }

    // Heatmap always tracks whatever markers were just drawn (same filtered
    // set), so toggling it on shows the same leads the cluster pins do -
    // weighted by opportunity score so hotter (more red) areas read as more
    // intense, not just "more pins".
    if (map.current) {
      if (heatLayer.current) map.current.removeLayer(heatLayer.current);
      const heatPoints = leadsData.map((lead) => [
        lead.location.lat,
        lead.location.lng,
        Math.max(0.25, (lead.digital_health_score || 0) / 100),
      ]);
      heatLayer.current = L.heatLayer(heatPoints, { radius: 28, blur: 22, maxZoom: 16 });
      if (showHeatmap) heatLayer.current.addTo(map.current);
    }
  };

  const toggleHeatmap = () => {
    if (!map.current) return;
    const next = !showHeatmap;
    if (heatLayer.current) {
      if (next) heatLayer.current.addTo(map.current);
      else map.current.removeLayer(heatLayer.current);
    }
    setShowHeatmap(next);
  };

  useEffect(() => {
    if (leads.length === 0) return;

    const activeFilterIds = filters.filter((f) => f.active).map((f) => f.id);

    const filteredLeads = leads.filter((lead) => {
      if (activeFilterIds.length === 0) return false;
      const gaps = lead.gap_labels || [];
      return activeFilterIds.some((id) => gaps.includes(GAP_MAPPING[id]));
    });

    updateMarkers(filteredLeads, false);
  }, [filters, leads]);

  const toggleFilter = (id) => {
    setFilters(filters.map((f) => (f.id === id ? { ...f, active: !f.active } : f)));
  };

  const setAllFilters = (state) => {
    setFilters(filters.map((f) => ({ ...f, active: state })));
  };

  // Live "how many of the currently-scanned leads match this gap" count per
  // filter row - independent of whether that filter is itself toggled on,
  // so switching one off still shows what you'd be bringing back.
  const filterCounts = useMemo(() => {
    const counts = {};
    for (const f of filters) {
      counts[f.id] = leads.filter((lead) => (lead.gap_labels || []).includes(GAP_MAPPING[f.id])).length;
    }
    return counts;
  }, [filters, leads]);

  return (
    <div className="h-full flex w-full bg-slate-950 overflow-hidden text-white font-sans" style={{ minHeight: '640px' }}>

      <div className="w-[340px] bg-slate-950/80 border-r border-white/[0.06] flex flex-col shadow-xl z-20">
        <div className="p-8 flex flex-col h-full overflow-hidden">

          <div className="mb-8 flex-shrink-0">
            <p className="text-[10px] font-extrabold text-slate-500 tracking-widest mb-1 uppercase">Intelligence Module</p>
            <h1 className="text-[28px] font-black text-white leading-none tracking-tight">Lead Scanner</h1>
          </div>

          <div className="mb-6 space-y-4 flex-shrink-0">
            <div ref={locationBoxRef} className="relative">
              <label className="text-[10px] font-bold text-slate-500 tracking-wider">LOCATION</label>
              <div className="relative mt-1.5">
                <input
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  onFocus={() => locationSuggestions.length > 0 && setShowLocationSuggestions(true)}
                  onKeyDown={handleLocationKeyDown}
                  autoComplete="off"
                  className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-xl text-sm font-bold text-white outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all"
                  placeholder="e.g. Mohali" />
                {locationLoading && (
                  <Loader2 size={14} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-500 animate-spin" />
                )}
              </div>

              {showLocationSuggestions && locationSuggestions.length > 0 && (
                <div className="absolute z-30 mt-1.5 w-full bg-slate-900 border border-white/[0.1] rounded-xl shadow-2xl overflow-y-auto max-h-60">
                  {locationSuggestions.map((s) => {
                    const [primary, ...rest] = s.description.split(',');
                    const secondary = rest.join(',').trim();
                    return (
                      <div key={s.place_id} onClick={() => selectLocationSuggestion(s)}
                        className="flex items-start gap-2.5 px-4 py-2.5 cursor-pointer hover:bg-white/[0.06] transition-colors border-b border-white/[0.04] last:border-b-0">
                        <MapPin size={14} className="text-slate-500 mt-0.5 shrink-0" />
                        <div className="min-w-0">
                          <div className="text-[13px] font-bold text-white truncate">{primary}</div>
                          {secondary && <div className="text-[11px] text-slate-500 truncate">{secondary}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div ref={targetBoxRef} className="relative">
              <label className="text-[10px] font-bold text-slate-500 tracking-wider">TARGET</label>
              <input
                value={type}
                onChange={(e) => setType(e.target.value)}
                onFocus={() => setShowTargetSuggestions(true)}
                autoComplete="off"
                className="w-full mt-1.5 px-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-xl text-sm font-bold text-white outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all"
                placeholder="e.g. Plumber" />

              {showTargetSuggestions && (
                <div className="absolute z-30 mt-1.5 w-full bg-slate-900 border border-white/[0.1] rounded-xl shadow-2xl max-h-72 overflow-y-auto">
                  {!type.trim() && recentIndustries.length > 0 && (
                    <div className="p-3 border-b border-white/[0.06]">
                      <span className="text-[9px] font-extrabold text-slate-500 tracking-widest uppercase">Recently used</span>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {recentIndustries.map((name) => (
                          <button key={name} onClick={() => selectIndustry(name)}
                            className="flex items-center gap-1 px-2.5 py-1 bg-white/[0.06] hover:bg-indigo-500/20 text-[11px] font-bold text-slate-300 hover:text-indigo-300 rounded-lg transition-colors">
                            <Clock size={10} /> {name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="p-2">
                    {!type.trim() && (
                      <span className="block px-2 py-1 text-[9px] font-extrabold text-slate-500 tracking-widest uppercase">All Industries</span>
                    )}
                    {filteredIndustries.length === 0 && (
                      <div className="px-3 py-2 text-[12px] text-slate-500">No matching industries - you can still type a custom one.</div>
                    )}
                    {filteredIndustries.map((name) => {
                      const active = name === type;
                      return (
                        <div key={name} onClick={() => selectIndustry(name)}
                          className={cn("flex items-center justify-between gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors",
                            active ? "bg-emerald-500/15 text-emerald-300" : "text-slate-300 hover:bg-white/[0.06]")}>
                          <span className="text-[12px] font-semibold truncate">{name}</span>
                          {active && <Check size={13} className="shrink-0" />}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* RADIUS - preset quick-picks plus a free-drag slider for any
                other value. Disabled (visually + pointer-events) while Full
                City is on, since deep_scan_places ignores it entirely. */}
            <div className={cn("transition-opacity", fullCity && "opacity-40 pointer-events-none")}>
              <div className="flex items-center justify-between">
                <label className="text-[10px] font-bold text-slate-500 tracking-wider">RADIUS</label>
                <span className="text-[11px] font-bold text-indigo-300">{radiusKm} km</span>
              </div>
              <div className="flex gap-2 mt-1.5">
                {RADIUS_PRESETS_KM.map((km) => (
                  <button key={km} onClick={() => setRadiusKm(km)}
                    className={cn("flex-1 py-2 rounded-lg text-[12px] font-bold border transition-all",
                      radiusKm === km ? "bg-indigo-500 border-indigo-500 text-white" : "bg-white/[0.04] border-white/[0.08] text-slate-300 hover:border-white/[0.2]")}>
                    {km}km
                  </button>
                ))}
              </div>
              <input
                type="range" min={1} max={50} step={1} value={radiusKm}
                onChange={(e) => setRadiusKm(Number(e.target.value))}
                className="w-full mt-2.5 accent-indigo-500" />
            </div>

            <div className="flex items-center justify-between pt-1">
              <div className="flex flex-col">
                <span className="text-[12px] font-bold text-white">Full City</span>
                <span className="text-[10px] text-slate-500">Ignore radius, scan the whole city</span>
              </div>
              <button onClick={() => setFullCity(!fullCity)}
                className={cn("relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none", fullCity ? "bg-indigo-500" : "bg-white/[0.12]")}>
                <span className="inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform" style={{ transform: fullCity ? 'translateX(18px)' : 'translateX(4px)' }} />
              </button>
            </div>
          </div>

          <div className="flex-1 flex flex-col min-h-0">
            <div className="flex justify-between items-end mb-4 flex-shrink-0">
              <span className="text-[11px] font-extrabold text-slate-500 tracking-widest">GAP FILTERS</span>
              <div className="flex gap-3 text-[11px] font-bold">
                <button onClick={() => setAllFilters(true)} className="text-indigo-400 hover:text-indigo-300">All</button>
                <button onClick={() => setAllFilters(false)} className="text-slate-500 hover:text-slate-300">None</button>
              </div>
            </div>

            <div className="space-y-2.5 overflow-y-auto pb-4 pr-1">
              {filters.map((filter) => (
                <div key={filter.id} onClick={() => toggleFilter(filter.id)}
                  className={cn("flex items-center justify-between p-4 rounded-[14px] border cursor-pointer transition-all duration-200",
                    filter.active ? "border-white/[0.1] bg-white/[0.04]" : "border-transparent bg-white/[0.015] hover:bg-white/[0.03]")}>
                  <div className="flex items-center gap-4">
                    <div className={cn("w-2 h-2 rounded-full", filter.color ? filter.color : "bg-transparent")}></div>
                    <span className={cn("font-bold text-[14px]", filter.active ? "text-white" : "text-slate-500")}>{filter.label}</span>
                    {leads.length > 0 && (
                      <span className={cn("px-1.5 py-0.5 rounded-md text-[10px] font-black tabular-nums",
                        filter.active ? "bg-indigo-500/15 text-indigo-300" : "bg-white/[0.04] text-slate-600")}>
                        {filterCounts[filter.id] ?? 0}
                      </span>
                    )}
                  </div>
                  <span className={cn("text-[11px] font-black tracking-wide", filter.active ? "text-white" : "text-slate-600")}>{filter.active ? 'ON' : 'OFF'}</span>
                </div>
              ))}
            </div>
          </div>

          {status && (
            <div className={cn("mb-4 p-3 rounded-xl text-[11px] font-bold text-center animate-pulse",
              status.includes('failed') || status.includes('timed out') ? "bg-rose-500/10 text-rose-400 border border-rose-500/20" : "bg-indigo-500/10 text-indigo-400 border border-indigo-500/20")}>
              {status}
            </div>
          )}

          <button onClick={handleScan} disabled={loading}
            className="mt-2 flex-shrink-0 w-full bg-indigo-600 hover:bg-indigo-500 active:scale-[0.98] text-white py-4 rounded-2xl font-bold text-lg flex justify-center items-center gap-2 shadow-[0_8px_20px_rgba(99,102,241,0.3)] transition-all disabled:opacity-70 disabled:cursor-not-allowed">
            {loading ? (
              <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
            ) : (
              <Send size={20} className="stroke-[2.5px]" />
            )}
            {loading ? 'Scanning...' : 'Scan Area'}
          </button>
        </div>
      </div>

      <div className="flex-1 relative h-full bg-slate-900">
        <div ref={mapContainer} className="w-full h-full z-0" />

        <div className="absolute top-6 left-6 z-[1000] bg-slate-900/90 border border-white/[0.08] backdrop-blur-md px-5 py-3.5 rounded-2xl shadow-lg flex items-center gap-3">
          <div className="w-2.5 h-2.5 rounded-full bg-indigo-400"></div>
          <span className="text-[11px] font-extrabold text-white tracking-widest uppercase">{city ? `${city} INTELLIGENCE HUB` : 'INTELLIGENCE HUB'}</span>
        </div>

        {/* Top-right control stack: stats panel, then a combined
            mode-toggle + zoom toolbar right below it - all floating, all
            top, nothing competing with the leads panel at the bottom. */}
        <div className="absolute top-6 right-6 z-[1000] flex flex-col items-end gap-3">
          <div className="bg-slate-900/90 border border-white/[0.08] backdrop-blur-md px-6 py-3.5 rounded-2xl shadow-lg flex gap-10">
            <div className="flex flex-col">
              <span className="text-[9px] font-extrabold text-slate-500 tracking-widest mb-0.5">COORDINATES</span>
              <span className="text-[13px] font-bold text-white">{coordinates}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-[9px] font-extrabold text-slate-500 tracking-widest mb-0.5">TOTAL SCANNED</span>
              <span className="text-[13px] font-bold text-white">{leads.length} Leads</span>
            </div>
          </div>

          <div className="bg-slate-900/90 border border-white/[0.08] backdrop-blur-md rounded-2xl shadow-lg flex items-center overflow-hidden">
            <button
              onClick={toggleMapMode}
              className="flex items-center gap-2 px-4 py-3 text-white hover:bg-white/[0.06] transition-all"
              title={mapMode === 'satellite' ? 'Switch to Map view' : 'Switch to Satellite view'}
            >
              {mapMode === 'satellite' ? <MapIcon size={16} className="text-indigo-400" /> : <Satellite size={16} className="text-indigo-400" />}
              <span className="text-[11px] font-extrabold tracking-widest uppercase">
                {mapMode === 'satellite' ? 'Map' : 'Satellite'}
              </span>
            </button>
            <div className="w-px self-stretch bg-white/[0.08]" />
            <button
              onClick={toggleHeatmap}
              disabled={leads.length === 0}
              className={cn("flex items-center gap-2 px-4 py-3 transition-all disabled:opacity-30 disabled:cursor-not-allowed",
                showHeatmap ? "bg-indigo-500/20 text-indigo-300" : "text-white hover:bg-white/[0.06]")}
              title={leads.length === 0 ? "Scan an area first" : showHeatmap ? "Hide density heatmap" : "Show density heatmap"}
            >
              <Flame size={16} className={showHeatmap ? "text-indigo-300" : "text-indigo-400"} />
              <span className="text-[11px] font-extrabold tracking-widest uppercase">Heat</span>
            </button>
            <div className="w-px self-stretch bg-white/[0.08]" />
            <button
              onClick={() => map.current?.zoomIn()}
              className="px-3.5 py-3 text-white hover:bg-white/[0.06] transition-all"
              title="Zoom in"
            >
              <Plus size={16} />
            </button>
            <div className="w-px self-stretch bg-white/[0.08]" />
            <button
              onClick={() => map.current?.zoomOut()}
              className="px-3.5 py-3 text-white hover:bg-white/[0.06] transition-all"
              title="Zoom out"
            >
              <Minus size={16} />
            </button>
          </div>
        </div>
      </div>

      {leads.length > 0 && (
        <div className="w-[380px] bg-slate-950/80 border-l border-white/[0.06] flex flex-col shadow-[-10px_0_30px_rgba(0,0,0,0.2)] z-20">
          <div className="p-6 border-b border-white/[0.06] bg-slate-950/60 shrink-0">
            <h2 className="text-xl font-black text-white">Found {leads.length} Leads</h2>
            <p className="text-sm text-slate-400 font-medium mt-1">Select a lead to view details</p>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-950/30">
            {leads.map((lead, idx) => (
              <div key={idx}
                className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-5 hover:bg-white/[0.05] transition-all duration-300 cursor-pointer hover:border-indigo-500/40 group"
                onClick={() => {
                  if (map.current) {
                    map.current.flyTo([lead.location.lat, lead.location.lng], 16, { animate: true, duration: 1 });
                  }
                }}>
                <div className="flex justify-between items-start mb-3 gap-2">
                  <h3 className="font-bold text-white group-hover:text-indigo-400 transition-colors line-clamp-2">{lead.name}</h3>
                  <div className={cn("px-2.5 py-1 rounded-lg text-xs font-bold whitespace-nowrap shrink-0",
                    lead.digital_health_score > 50 ? "bg-rose-500/10 text-rose-400" :
                      lead.digital_health_score > 20 ? "bg-orange-500/10 text-orange-400" :
                        "bg-emerald-500/10 text-emerald-400")}>
                    Score: {lead.digital_health_score}
                  </div>
                </div>

                <div className="space-y-2 text-sm text-slate-400 mb-4">
                  {lead.rating > 0 && (
                    <div className="flex items-center gap-2">
                      <Star size={14} className="text-amber-400 fill-amber-400" />
                      <span className="font-semibold text-slate-300">{lead.rating} <span className="text-slate-500 font-normal">({lead.reviews_count} reviews)</span></span>
                    </div>
                  )}
                  <div className="flex items-start gap-2">
                    <MapPin size={14} className="text-slate-500 mt-0.5 shrink-0" />
                    <span className="line-clamp-2 text-xs leading-relaxed">{lead.address}</span>
                  </div>
                  {lead.phone && (
                    <div className="flex items-center gap-2">
                      <Phone size={14} className="text-slate-500" />
                      <span className="text-xs font-medium text-slate-300">{lead.phone}</span>
                    </div>
                  )}
                  {lead.website && (
                    <div className="flex items-center gap-2 overflow-hidden">
                      <Globe size={14} className="text-slate-500 shrink-0" />
                      <a href={lead.website.startsWith('http') ? lead.website : `https://${lead.website}`} target="_blank" rel="noreferrer" className="text-indigo-400 hover:underline text-xs truncate" onClick={(e) => e.stopPropagation()}>
                        {lead.website.replace(/^https?:\/\//, '')}
                      </a>
                    </div>
                  )}
                  {lead.email && (
                    <div className="flex items-center gap-2 overflow-hidden">
                      <Mail size={14} className="text-slate-500 shrink-0" />
                      <span className="text-xs truncate">{lead.email}</span>
                    </div>
                  )}
                </div>

                {lead.gap_labels && lead.gap_labels.length > 0 && (
                  <div className="pt-3 border-t border-white/[0.06] flex flex-wrap gap-1.5">
                    {lead.gap_labels.map((gap, i) => (
                      <span key={i} className="px-2 py-1 bg-white/[0.06] text-slate-400 text-[10px] font-bold uppercase tracking-wider rounded-md">{gap}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}
